import React, { useState, useEffect } from "react";
import './main.css';

const API_KEY = "bd09cb93d8c19b18c037d66942c3a38da86bdeee";

const AirAware = () => {
  const [location, setLocation] = useState("");
  const [aqiData, setAqiData] = useState(null);
  const [error, setError] = useState(null);
  const [tipsChecked, setTipsChecked] = useState({
    wearMask: false,
    limitOutdoor: false,
    stayIndoors: false,
  });
  const [readyToGo, setReadyToGo] = useState(false);

  const fetchAQI = async (city) => {
    try {
      const response = await fetch(`https://api.waqi.info/feed/${city}/?token=${API_KEY}`);
      const data = await response.json();
      if (data.status === "ok") {
        setAqiData(data.data);
        setError(null);
        setReadyToGo(false);  // Reset when new data is fetched
        setTipsChecked({ wearMask: false, limitOutdoor: false, stayIndoors: false });
      } else {
        setError("Location not found");
        setAqiData(null);
      }
    } catch (err) {
      setError("Error fetching data");
    }
  };

  const getHealthAdvice = (aqi) => {
    if (aqi <= 50) return "Air quality is good. Enjoy your day!";
    if (aqi <= 100) return "Moderate air quality. Sensitive groups should take caution";
    if (aqi <= 150) return "Unhealthy for sensitive groups. Reduce outdoor activity";
    if (aqi <= 200) return "Unhealthy air quality. Limit outdoor exposure";
    if (aqi <= 300) return "Very unhealthy. Avoid outdoor activities";
    return "Hazardous! Stay indoors and wear a mask";
  };

  const healthTips = () => {
    return (
      <div className="mt-4">
        <h3 className="text-lg font-semibold">Health Tips:</h3>
        <ul className="list-inside list-disc mt-2">
          <li>
            <input
              type="checkbox"
              checked={tipsChecked.wearMask}
              onChange={() => setTipsChecked({ ...tipsChecked, wearMask: !tipsChecked.wearMask })}
            />
            Wear a mask if the air quality is poor
          </li>
          <li>
            <input
              type="checkbox"
              checked={tipsChecked.limitOutdoor}
              onChange={() => setTipsChecked({ ...tipsChecked, limitOutdoor: !tipsChecked.limitOutdoor })}
            />
            Limit outdoor activities if air quality is unhealthy
          </li>
          <li>
            <input
              type="checkbox"
              checked={tipsChecked.stayIndoors}
              onChange={() => setTipsChecked({ ...tipsChecked, stayIndoors: !tipsChecked.stayIndoors })}
            />
            Stay indoors if the air quality is hazardous
          </li>
        </ul>
        {Object.values(tipsChecked).every(Boolean) && (
          <p className="text-green-500 mt-2 font-semibold">You're ready to go!</p>
        )}
      </div>
    );
  };

  const getLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(async (position) => {
        const latitude = position.coords.latitude;
        const longitude = position.coords.longitude;
        const response = await fetch(`https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`);
        const locationData = await response.json();
        const city = locationData.city || locationData.locality;
        setLocation(city);
        fetchAQI(city);
      }, () => {
        setError("Unable to retrieve your location.");
      });
    } else {
      setError("Geolocation is not supported by this browser.");
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-100 p-6">
      <h1 className="text-3xl font-bold mb-4">Air Aware</h1>
      <input
        type="text"
        placeholder="Enter city name ..."
        value={location}
        onChange={(e) => setLocation(e.target.value)}
        className="p-2 border border-gray-300 rounded-md"
      />
      <button
        onClick={() => fetchAQI(location)}
        className="mt-2 px-4 py-2 bg-blue-500 text-white rounded-md"
      >
        Get AQI
      </button>
      <button
        onClick={getLocation}
        className="mt-2 px-4 py-2 bg-green-500 text-white rounded-md"
      >
        Get Current Location AQI
      </button>
      {error && <p className="text-red-500 mt-2">{error}</p>}
      {aqiData && (
        <div className="mt-4 p-4 bg-white shadow-md rounded-lg text-center">
          <h2 className="text-xl font-semibold">{aqiData.city.name}</h2>
          <p className="text-2xl font-bold">AQI: {aqiData.aqi}</p>
          <p className="mt-2">{getHealthAdvice(aqiData.aqi)}</p>
          {healthTips()}
        </div>
      )}
    </div>
  );
};

export default AirAware;
